import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { apiService, ApiError } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { CreditHistoryResponse, CreditHistoryEntry } from '../../types/api';
import { Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export function CreditHistory() {
  const { user } = useAuth();
  const [history, setHistory] = useState<CreditHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBureau, setSelectedBureau] = useState<string>('TransUnion');

  const fetchHistory = async () => {
    try {
      setIsLoading(true);
      setError('');
      const bureauCount = user?.subscription ? 3 : 1;
      const data = await apiService.getCreditHistory(bureauCount);
      setHistory(data);
      // Set initial selected bureau to the first available one
      if (data && Object.keys(data.bureau).length > 0) {
        setSelectedBureau(Object.keys(data.bureau)[0]);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch credit history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [user?.subscription]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatChartDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    } catch {
      return dateString;
    }
  };

  const getScoreTrend = (entry: CreditHistoryEntry) => {
    // Use scoreNew and scoreOld from the entry if available
    if (entry.scoreNew !== undefined && entry.scoreOld !== undefined) {
      // If scoreOld is 0, display nothing (will show "-" in the UI)
      if (entry.scoreOld === 0) {
        return null;
      }
      
      const diff = entry.scoreNew - entry.scoreOld;
      
      if (diff > 0) {
        return { icon: <TrendingUp className="h-4 w-4 text-green-600" />, text: `+${diff}`, color: 'text-green-600' };
      } else if (diff < 0) {
        return { icon: <TrendingDown className="h-4 w-4 text-red-600" />, text: `${diff}`, color: 'text-red-600' };
      } else {
        return { icon: <Minus className="h-4 w-4 text-muted-foreground" />, text: '0', color: 'text-muted-foreground' };
      }
    }
    
    return null;
  };

  // Prepare chart data by combining all bureaus
  const prepareChartData = () => {
    if (!history) return [];
    
    // Group data by year-month
    const monthMap = new Map<string, any>();
    
    Object.entries(history.bureau).forEach(([bureau, entries]) => {
      entries.forEach((entry) => {
        const monthKey = formatChartDate(entry.reportTime);
        
        if (!monthMap.has(monthKey)) {
          monthMap.set(monthKey, {
            date: monthKey,
            fullDate: entry.reportTime, // Keep one date for sorting
          });
        }
        
        // Use the most recent score for this month for this bureau
        const existingScore = monthMap.get(monthKey)[bureau];
        const newScore = entry.scoreNew;
        
        // If no score exists yet, or this entry is more recent, use it
        if (existingScore === undefined || new Date(entry.reportTime) > new Date(monthMap.get(monthKey).fullDate)) {
          monthMap.get(monthKey)[bureau] = newScore;
          monthMap.get(monthKey).fullDate = entry.reportTime;
        }
      });
    });
    
    // Convert to array and sort by date (oldest first for chart)
    return Array.from(monthMap.values())
      .sort((a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime());
  };

  const chartData = prepareChartData();
  const bureauNames = history ? Object.keys(history.bureau) : [];
  
  // Colors for different bureaus (matching screenshot)
  const bureauColors: Record<string, string> = {
    'TransUnion': '#06B6D4', // Cyan
    'Experian': '#A21CAF', // Fuchsia
    'Equifax': '#BE123C', // Rose
  };

  // Get the latest scores for each bureau
  const getLatestScores = () => {
    if (!history) return {};
    
    const latestScores: Record<string, number | null> = {};
    
    Object.entries(history.bureau).forEach(([bureau, entries]) => {
      if (entries.length > 0) {
        // Get the most recent entry
        const latestEntry = entries[0];
        latestScores[bureau] = latestEntry.scoreNew ?? latestEntry.scoreOld ?? null;
      }
    });
    
    return latestScores;
  };

  // Get the latest update date
  const getLatestUpdateDate = (): Date | null => {
    if (!history) return null;
    
    let latestDate: Date | null = null;
    
    Object.values(history.bureau).forEach((entries) => {
      if (entries.length > 0) {
        const entryDate = new Date(entries[0].reportTime);
        if (!latestDate || entryDate > latestDate) {
          latestDate = entryDate;
        }
      }
    });
    
    return latestDate;
  };

  const latestScores = getLatestScores();
  const latestUpdateDate = getLatestUpdateDate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading credit history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-destructive">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!history || Object.keys(history.bureau).length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-muted-foreground">No credit history available yet.</p>
            <p className="text-sm text-muted-foreground mt-2">
              History will be available after your next credit report refresh.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white border border-gray-200 p-6" style={{ borderRadius: '8px' }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold text-gray-900">Credit Score History</h3>
            {latestUpdateDate && (
              <span className="text-sm text-muted-foreground">
                Last Updated: {latestUpdateDate.toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            )}
          </div>
          
          {/* Bureau Scores Display - inside chart frame */}
          <div className="flex items-center gap-6 mb-4">
            {bureauNames.map((bureau) => {
              const score = latestScores[bureau];
              return (
                <div key={bureau} className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{bureau}:</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {score ?? 'N/A'}
                  </span>
                </div>
              );
            })}
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                className="text-xs"
                tick={{ fill: '#6b7280', fontSize: 12 }}
                tickMargin={10}
              />
              <YAxis 
                domain={[400, 850]}
                className="text-xs"
                tick={{ fill: '#6b7280', fontSize: 12 }}
                tickMargin={10}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ color: '#111827', fontWeight: 600 }}
              />
              <Legend 
                wrapperStyle={{
                  paddingTop: '20px',
                  fontSize: '12px',
                }}
                iconType="circle"
                iconSize={8}
                formatter={(value) => <span style={{ color: '#000', verticalAlign: 'middle' }}>{value}</span>}
                verticalAlign="bottom"
              />
              {bureauNames.map((bureau) => (
                <Line
                  key={bureau}
                  type="monotone"
                  dataKey={bureau}
                  stroke={bureauColors[bureau]}
                  strokeWidth={2}
                  dot={{ 
                    r: 3, 
                    fill: 'white',
                    stroke: bureauColors[bureau],
                    strokeWidth: 2
                  }}
                  activeDot={{ 
                    r: 4,
                    fill: 'white',
                    stroke: bureauColors[bureau],
                    strokeWidth: 2
                  }}
                  name={bureau}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Bureau Tabs and Table */}
      <Tabs value={selectedBureau} onValueChange={setSelectedBureau} className="w-full">
        {/* Table for Selected Bureau */}
        {bureauNames.map((bureau) => (
          <TabsContent key={bureau} value={bureau} className="mt-0">
            {history?.bureau[bureau] && history.bureau[bureau].length > 0 ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <Table>
                  <TableHeader>
                    {/* First Header Row - Tabs Switcher */}
                    <TableRow className="border-b-0">
                      <TableHead colSpan={4} className="px-4 py-3">
                        <TabsList className="flex flex-wrap justify-start bg-transparent p-0 gap-2 h-auto">
                          {bureauNames.map((b) => (
                            <TabsTrigger 
                              key={b}
                              value={b}
                              className="px-3 py-1.5 text-sm font-medium rounded-md bg-white data-[state=active]:bg-gray-100 whitespace-nowrap"
                            >
                              {b}
                            </TabsTrigger>
                          ))}
                        </TabsList>
                      </TableHead>
                    </TableRow>
                    
                    {/* Second Header Row - Column Headers */}
                    <TableRow className="border-b-2 border-b-gray-200 border-t border-t-gray-200">
                      <TableHead className="pl-4 pr-2 py-2 text-sm font-medium w-[180px]" style={{ color: '#737373', fontWeight: 500 }}>
                        Date
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium w-[120px]" style={{ color: '#737373', fontWeight: 500 }}>
                        Score
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium w-[120px]" style={{ color: '#737373', fontWeight: 500 }}>
                        Change
                      </TableHead>
                      <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                        Notable Changes
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.bureau[bureau].map((entry, index) => {
                      const trend = getScoreTrend(entry);
                      const displayScore = entry.scoreNew ?? entry.scoreOld ?? 'N/A';
                      return (
                        <TableRow 
                          key={`${entry.reportTime}-${index}`} 
                          className="border-b border-gray-200 first:border-t first:border-t-gray-200 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                        >
                          <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">
                            {formatDate(entry.reportTime)}
                          </TableCell>
                          <TableCell className="px-2 py-3 text-xl font-bold text-gray-900">
                            {displayScore}
                          </TableCell>
                          <TableCell className="px-2 py-3">
                            {trend ? (
                              <span className={`text-sm font-semibold ${trend.color}`}>
                                {trend.text}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-500">-</span>
                            )}
                          </TableCell>
                          <TableCell className="px-2 py-3">
                            {entry.changes && entry.changes.length > 0 ? (
                              <div className="space-y-2">
                                {entry.changes.map((change, changeIndex) => (
                                  <div key={changeIndex}>
                                    <div className="font-medium text-gray-900 text-sm">{change.title}</div>
                                    {change.summary && (
                                      <div className="text-gray-600 text-xs">{change.summary}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No history available for {bureau}</p>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

